import * as React from "react";
import { Card, CardBody, CardImage } from "@progress/kendo-react-layout";
import { Form, Field, FormElement, FormRenderProps } from "@progress/kendo-react-form";
import { Button } from "@progress/kendo-react-buttons";
import { Input } from "@progress/kendo-react-inputs";
import { useInjection } from "@presentation/components/dependencyInjection";
import { useNotifications } from "@presentation/components/notifications";
import localStyles from "@presentation/components/loginCard/LoginCard.module.scss";
import { useState, useRef, useLayoutEffect } from "react";
import { useTranslation } from "react-i18next";
import { API_ENDPOINTS } from "@infrastructure/InfrastructureConstants";
import { userIcon } from "@progress/kendo-svg-icons";
import { signInProgress } from "@presentation/components/helpers/constants";
import { runtimeConfig } from "@utils/runtimeConfig/runtimeConfig";
import { UserCredentials } from "@domain/schemas/BI";
import { AuthService } from "@infrastructure/services";
import { useNavigate } from "react-router-dom";

/**
 * Handles the OIDC login by redirecting the user to the OIDC endpoint.
 */
const handleOidcLogin = () => {
  sessionStorage.setItem(signInProgress, "true");
  const redirectUri = `${location.origin}/auth/callback`;
  location.assign(`${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.oidc}?redirect_uri=${encodeURIComponent(redirectUri)}`);
};

/**
 * Component with the login feature, accepting username and password credentials to create a new session.
 * @returns The LoginCard component.
 */
const LoginCard: React.FC = () => {
  const authService = useInjection(AuthService);
  const { pushNotification } = useNotifications();
  const { t: loc } = useTranslation();
  const navigate = useNavigate();
  const [loginFailedMessage, setLoginFailedMessage] = useState("");
  const [isSigningIn, setIsSigningIn] = useState(false);
  const submitButtonRef = useRef<Button>(null);

  // Focus the submit button when the component mounts
  useLayoutEffect(() => {
    if (submitButtonRef.current) {
      submitButtonRef.current.element?.focus();
    }
  }, []);

  /**
   * Handles the form submission for user login.
   * @param values - The form values containing username and password.
   */
  const handleSubmit = async (values: { [name: string]: any }) => {
    if (isSigningIn) {
      return;
    }

    const userCredentials: UserCredentials = {
      username: values.username,
      password: values.password,
    };

    setIsSigningIn(true);
    setLoginFailedMessage("");

    try {
      await authService.signInWithCredentials(userCredentials);
      sessionStorage.removeItem(signInProgress);
      console.info("Credentials login successful");
      // Navigate within the SPA. A full reload would discard the session that was
      // just established, because the user session store is held in memory only.
      navigate("/", { replace: true });
    } catch (error) {
      sessionStorage.removeItem(signInProgress);
      const problem = error as { response?: { data?: { detail?: string; title?: string } }; message?: string };
      const message = problem?.response?.data?.detail || problem?.response?.data?.title || problem?.message || "Login failed.";
      pushNotification(message, "error", 5000);
      setLoginFailedMessage(message);
      setIsSigningIn(false);
    }
  };

  return (
    <div className={localStyles.centered}>
      <Card
        className={localStyles.card_container}
        style={{
          width: "600px",
          minHeight: "600px",
          padding: "30px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <CardBody>
          <div className={localStyles.centered}>
            <CardImage
              src="/omicsFlowLogo_RGB2_16x16.svg"
              className={localStyles.medium_logo}
              alt="logo"
              style={{ width: "250px", height: "auto" }} // Adjusted logo size for the larger card
            />
          </div>
          <br />
          <div className={localStyles.centered}>
            <Form
              onSubmit={handleSubmit}
              initialValues={{ username: "", password: "" }}
              render={(formRenderProps: FormRenderProps) => (
                <FormElement>
                  <Field id={"username"} name={"username"} component={Input} label={loc("username")} />
                  <Field id={"password"} name={"password"} type={"password"} label={loc("password")} component={Input} />
                  {loginFailedMessage && <div className={`${localStyles.error_message} ${localStyles.centered}`}>{loginFailedMessage}</div>}
                  <div className={`${localStyles.padding_top}`}>
                    <Button
                      type={"submit"}
                      disabled={isSigningIn || !formRenderProps.allowSubmit || !formRenderProps.valueGetter("username")?.trim() || !formRenderProps.valueGetter("password")?.trim()}
                      themeColor={"primary"}
                      ref={submitButtonRef}
                    >
                      {loc("submit")}
                    </Button>
                  </div>
                </FormElement>
              )}
            />
          </div>
          <div className={`${localStyles.centered} ${localStyles.padding_top}`}>
            <Button svgIcon={userIcon} className={localStyles.oidc_login_button} type="button" themeColor="primary" onClick={handleOidcLogin}>
              <span className={localStyles.redirect_arrow}>&#10532;</span>
              {loc("oidcSignIn")}
            </Button>
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default LoginCard;
